/**
 * Chand Raat mehndi — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const chandRaatMehndiPack: ToilePack = {
    slug: 'chand-raat-mehndi',
    category: 'tattoo',
    name: 'Chand Raat mehndi',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'quick', label: 'Quick stall', subject: 'a fast back-hand design of one large round flower near the knuckles with three leaves and a line of dots running to the wrist'},
        {key: 'bangle', label: 'Bangle band', subject: 'a band of mehndi drawn like stacked bangles around the wrist, with a small crescent on the back of the hand and fingertip caps'},
        {key: 'moon', label: 'Moon sighting', subject: 'a back-hand design with a thin crescent and a few stars over the knuckles, a lacy net below and a single vine on the middle finger'},
    ],
    styles: [
        {key: 'cone', label: 'Fine cone', style: 'drawn in one fine continuous cone line, small leaves and dots, the kind finished in five minutes', palette: 'reddish-brown henna line on a pure white background, no other color', framing: 'the hand centered on the page with a white margin'},
        {key: 'tikki', label: 'Bold tikki', style: 'bold stall work around a solid filled round tikki, thick strokes and chunky leaves', palette: 'black ink only on a pure white background', framing: 'the hand centered on the page with a white margin'},
        {key: 'stencil', label: 'Stencil', style: 'drawn as a cut-out stencil sheet, every motif a solid black shape joined by small bridges so it lifts in one piece', palette: 'solid black shapes on a pure white background', framing: 'the hand centered on the page with a white margin'},
    ],
    directions: ['on feet', 'for kids', 'both hands'],
    directionHint: 'try: on feet',
    seo: {
        title: 'Chand Raat mehndi designs: quick stall, bangles, moon',
        metaDescription: 'Nine printable Chand Raat mehndi designs on a hand outline: quick flowers, bangle bands and a moon sighting, in three finishes.',
        h1: 'Chand Raat mehndi: quick stall, bangle band, moon sighting',
        tagline: 'Nine hand outlines, three quick designs each as fine cone, bold tikki and stencil.',
        intro: 'Quick mehndi designs for Chand Raat, the night before Eid, when the moon is sighted and the bazaars fill with bangles and mehndi stalls. The sheets show designs of the kind done in a few minutes: one large round flower with a line of dots to the wrist, a band drawn like stacked bangles, a thin crescent and stars over the knuckles. Each comes as a fine cone line, a bold tikki or a stencil.',
        useCases: [
            'a design done at home the night before Eid',
            'a quick design to copy at a stall',
            'a design for a sister and cousins',
            'a stencil for a quick application',
        ],
        faq: [
            {q: 'What is a tikki?', a: 'The solid, filled round motif at the centre of the bold stall design.'},
            {q: 'How does the stencil work?', a: 'Every motif is a solid black shape joined to the others by small bridges, so the stencil lifts in one piece.'},
            {q: 'Are these designs quick?', a: 'Yes. They are the kind finished in a few minutes at a stall or at home.'},
            {q: 'Can I ask for a different hand or design?', a: 'Yes. Type a direction such as on feet and the hand is drawn for it. That is a request, not a promised result.'},
        ],
    },
}
